import React, { useCallback, useMemo, useState } from 'react';
import {
  useSelector as useReduxSelector,
  TypedUseSelectorHook,
} from 'react-redux';
import { AppState, useAppDispatch } from 'stores/type';
import { DefaultTemplate } from 'views/templates/DefaultTemplate';
import { DeviceDomain } from 'models/Device';
import { NotificationSettingPageLayout } from 'views/layouts/NotificationSettingPageLayout';
import { useHistory } from 'react-router-dom';
import { localeIds } from 'locals';
import { useIntl } from 'react-intl';
import { setExceptionError } from 'stores/errorSlice';
import { ApplicationError } from 'models/Error';
import { ROUTER_PATH } from 'views/constants';
import { useDevice } from 'views/hooks/useDevice';

export const NotificationSettingPage: React.FC = () => {
  const history = useHistory();
  const useSelector: TypedUseSelectorHook<AppState> = useReduxSelector;
  const device = useSelector((state) => state.deviceState.device);
  const deviceDomain = useMemo(() => {
    return new DeviceDomain(device);
  }, [device]);
  const [notificationMessage, setNotificationMessage] = useState('');
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const { deleteEmail, registerEmail } = useDevice();

  const handleSave = useCallback(
    async (email: string) => {
      await registerEmail(deviceDomain, email)
        .then(() => {
          setNotificationMessage(
            intl.formatMessage(
              {
                id: localeIds.notificationSettingPage.sendEmailActivation,
              },
              { email }
            )
          );
        })
        .catch((e) => {
          // ApplicationError は useDevice 側で setErrorMessageLocaleId により
          // ローカライズ済みメッセージを表示済みの既知エラーなので例外として扱わない
          if (!(e instanceof ApplicationError)) {
            dispatch(setExceptionError(e));
          }
        });
    },
    [deviceDomain, dispatch, intl, registerEmail]
  );

  const handleDelete = useCallback(async () => {
    if (
      confirm(
        intl.formatMessage({
          id: localeIds.notificationSettingPage.confirm.delete,
        })
      )
    ) {
      await deleteEmail(deviceDomain)
        .then(() => history.push(ROUTER_PATH.apps))
        .catch((e) => {
          if (!(e instanceof ApplicationError)) {
            dispatch(setExceptionError(e));
          }
        });
    }
  }, [intl, deleteEmail, deviceDomain, history, dispatch]);

  return (
    <DefaultTemplate
      headerText={intl.formatMessage({
        id: localeIds.notificationSettingPageLayout.title,
      })}
      onBack={(): void => {
        history.goBack();
      }}
    >
      <NotificationSettingPageLayout
        currentEmail={device?.email}
        notificationMessage={notificationMessage}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    </DefaultTemplate>
  );
};
